import { ErrorMessage } from '@hookform/error-message';
import { useMemo } from 'react';
import { Control, Controller, FieldPathByValue, FieldValues, useController } from 'react-hook-form';

import type { Borrower } from '@willow/graphql-iso/src/app';
import { getBorrowerNameWithoutMiddleName } from '@willow/shared-iso';
import { BorrowerId } from '@willow/types-iso';

import { Form } from '../../../../bootstrap';
import { Options, WillowSelect } from './WillowSelect';

type Props<T extends FieldValues> = {
  borrowers: Borrower[];
  fieldName: FieldPathByValue<T, BorrowerId | undefined>;
  control: Control<T>;
  allowAllSelections?: boolean; // some components can select even if email / consent is not provided
  hideErrorMessage?: boolean;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
};

export function ControlledBorrowerSelect<T extends FieldValues>({
  borrowers,
  fieldName,
  control,
  allowAllSelections = true,
  hideErrorMessage = false,
  label,
  placeholder,
  required,
  disabled,
}: Props<T>) {
  const {
    formState: { errors },
  } = useController({ control, name: fieldName });

  const options: Options = useMemo(
    () =>
      borrowers.map((borrower) => {
        const disabledMessage = !borrower.email
          ? 'email not provided'
          : !borrower.emailConsent
          ? 'email consent disabled'
          : undefined;
        return {
          value: borrower.id,
          label: (
            <>
              {getBorrowerNameWithoutMiddleName(borrower)}{' '}
              {disabledMessage && <span className="u-color-red1 fst-italic">({disabledMessage})</span>}
            </>
          ),
          isDisabled: !allowAllSelections && disabledMessage != null,
        };
      }),
    [borrowers, allowAllSelections],
  );
  return (
    <>
      {label && (
        <Form.Label className="u-fs-2 fw-bold">
          {label}
          {required && <span className="u-color-red1 ps-1 ">*</span>}
        </Form.Label>
      )}
      <Controller
        name={fieldName}
        control={control}
        render={({ field: { onChange, value } }) => (
          <WillowSelect
            required={required}
            isDisabled={disabled}
            placeholder={placeholder || 'Select recipient'}
            options={options}
            value={value ? options.filter((option) => value === option.value) : undefined}
            isClearable
            onChange={(e) => onChange(e?.value ?? undefined)}
          />
        )}
      />
      {!hideErrorMessage && (
        <ErrorMessage
          errors={errors}
          name={fieldName as any}
          render={({ message }) => <div className="u-fs-2 u-color-red1">{message}</div>}
        />
      )}
    </>
  );
}
